/**
 * ReadyPushErrorsPanel — Push-Fehler-Liste + „Erneut senden" im Ergebnis-Panel
 * (T14/P2, Fund aus T8). Dedupliziert `suggestion_ids` aus `push_errors`,
 * ruft `retryPushSuggestions`, ersetzt die lokale Liste durch die
 * verbleibenden `push_errors` der Antwort. Ausgelagert aus
 * `ReadyResultPanel.tsx` wegen des Component-Size-Limits.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EID } from '@/lib/element-ids.registry'
import type { PushError } from '@/lib/api/channel-content-bulk'

import { useUndoRetryMutations } from '../hooks/useUndoRetryMutations'

export interface ReadyPushErrorsPanelProps {
  pushErrors: PushError[]
  onResolved: (next: PushError[]) => void
}

export function ReadyPushErrorsPanel({ pushErrors, onResolved }: ReadyPushErrorsPanelProps) {
  const { t } = useTranslation()
  const { retryPush } = useUndoRetryMutations()
  const [pushedOk, setPushedOk] = useState(0)

  const handleRetry = () => {
    const ids = [...new Set(pushErrors.map((e) => e.suggestion_id))]
    retryPush.mutate(
      { suggestion_ids: ids },
      {
        onSuccess: (r) => {
          setPushedOk(r.pushed_ok)
          onResolved(r.push_errors)
        },
      },
    )
  }

  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">{t('channelContent.ready.pushErrors', { count: pushErrors.length })}</p>
        <button
          type="button"
          id={EID['text-bulk-ready-retry-push-btn']}
          disabled={retryPush.isPending}
          onClick={handleRetry}
          className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/50"
        >
          {t('channelContent.ready.retryPush', 'Erneut senden')}
        </button>
      </div>
      {pushedOk > 0 && (
        <p className="mt-1.5 text-xs text-green-700 dark:text-green-400">
          {t('channelContent.ready.retryPushed', '{{count}} erneut übertragen', { count: pushedOk })}
        </p>
      )}
      <ul className="mt-1.5 space-y-1">
        {pushErrors.map((err) => (
          <li key={err.suggestion_id} className="text-xs">
            {err.block_type ?? err.suggestion_id}: {err.error}
          </li>
        ))}
      </ul>
    </div>
  )
}
